import React, { useCallback, useEffect, useMemo } from 'react';
import Zoom from '@uber/base-icons/zoom-in';
import { useStyletron } from 'baseui';
import type { ButtonOverrides } from 'baseui/button';
import { PLACEMENT, StatefulPopover, TRIGGER_TYPE } from 'baseui/popover';
import { StatefulTooltip } from 'baseui/tooltip';
import useShortcut from '../../image/labelling/providers/hooks/use-shortcut';
import { handleShortcutRegistrationError } from '../../image/labelling/providers/utils/shortcut';
import { IconButton } from './icon-button';
import { VerticalSlider } from './vertical-slider';
const DEFAULT_ZOOM_VALUE = 0;
const ZOOM_CHANGE = 10;
export type PlayerZoomProps = {
  zoom: number;
  onZoomChange: (value: number) => void;
  onFinalChange?: (value: number) => void;
  min?: number;
  max?: number;
  iconSize?: number;
  buttonOverrides?: ButtonOverrides;
  tooltipContent?: string;
};
export const PlayerZoom = ({
  zoom,
  onZoomChange,
  onFinalChange,
  min = 0,
  max = 100,
  iconSize = 16,
  buttonOverrides,
  tooltipContent = 'Zoom'
}: PlayerZoomProps) => {
  const [css, theme] = useStyletron();
  const {
    registerHotKeys
  } = useShortcut();
  const handleZoomOut = useCallback(() => {
    if (zoom > min) {
      const deltaZoom = zoom - ZOOM_CHANGE;
      const zoomOut = deltaZoom >= min ? deltaZoom : min;
      onZoomChange(zoomOut);
    }
  }, [onZoomChange, zoom, min]);
  const handleZoomIn = useCallback(() => {
    if (zoom < max) {
      const deltaZoom = zoom + ZOOM_CHANGE;
      const zoomIn = deltaZoom <= max ? deltaZoom : max;
      onZoomChange(zoomIn);
    }
  }, [onZoomChange, zoom, max]);
  const keyboardEventList = useMemo(() => {
    return [{
      keys: ['-'],
      onAction: handleZoomOut
    }, {
      keys: ['='],
      // using = instead of +
      onAction: handleZoomIn
    }];
  }, [handleZoomIn, handleZoomOut]);
  useEffect(() => {
    const {
      errors,
      removeHotKeys
    } = registerHotKeys(keyboardEventList);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [keyboardEventList, registerHotKeys]);
  return <StatefulTooltip content={tooltipContent} placement="bottom" showArrow>
      <StatefulPopover placement={PLACEMENT.top} triggerType={TRIGGER_TYPE.click} overrides={{
      Body: {
        style: {
          zIndex: 5
        }
      }
    }} content={<div className={css({
      height: theme.sizing.scale3200,
      width: theme.sizing.scale950,
      background: theme.colors.backgroundSecondary,
      borderRadius: theme.borders.radius300,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: theme.sizing.scale100
    })}>
            <VerticalSlider min={min} max={max} value={zoom} onChange={onZoomChange} onFinalChange={onFinalChange} />
          </div>} accessibilityType={'tooltip'}>
        <IconButton overrides={buttonOverrides}>
          <Zoom size={iconSize} title={tooltipContent} />
        </IconButton>
      </StatefulPopover>
    </StatefulTooltip>;
};
export { DEFAULT_ZOOM_VALUE, ZOOM_CHANGE };